import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CountdownTimer } from '@/components/CountdownTimer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DropsPage() {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    fetchDrops();
  }, []);

  const fetchDrops = async () => {
    try {
      const response = await axios.get(`${API}/drops`);
      setDrops(response.data);
    } catch (error) {
      console.error('Failed to fetch drops:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotify = async (productId) => {
    if (!notifyEmail) {
      toast.error('Please enter your email');
      return;
    }

    try {
      await axios.post(`${API}/drops/notify`, {
        email: notifyEmail,
        product_id: productId
      });
      toast.success('You will be notified when this drop is available');
      setNotifyEmail('');
      setSelectedProduct(null);
    } catch (error) {
      toast.error('Failed to register for notification');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const upcomingDrops = drops.filter(d => d.drop_date && new Date(d.drop_date) > new Date());
  const availableDrops = drops.filter(d => !d.drop_date || new Date(d.drop_date) <= new Date());

  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="drops-page">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Limited Releases</span>
          <h1 className="font-['Syne'] text-4xl md:text-6xl font-bold mt-2">Drops</h1>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto">
            Exclusive limited edition pieces. Once they're gone, they're gone.
          </p>
        </div>

        {/* Upcoming Drops */}
        {upcomingDrops.length > 0 && (
          <section className="mb-20">
            <h2 className="font-['Syne'] text-2xl font-bold mb-8">Upcoming Drops</h2>
            
            <div className="space-y-8">
              {upcomingDrops.map((product) => (
                <div 
                  key={product.id}
                  className="grid md:grid-cols-2 gap-8 border border-neutral-800 p-6"
                  data-testid={`upcoming-drop-${product.id}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/600'}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <span className="text-[#FF3B30] uppercase text-xs tracking-widest mb-2">
                      Coming Soon
                    </span>
                    <h3 className="font-['Syne'] text-2xl md:text-3xl font-bold mb-4">
                      {product.name}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {product.description}
                    </p>
                    <p className="text-2xl font-bold mb-6">
                      {formatPrice(product.price)}
                    </p>
                    
                    <div className="mb-8">
                      <p className="text-sm text-muted-foreground mb-3">Drops in:</p>
                      <CountdownTimer targetDate={product.drop_date} />
                    </div>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          className="btn-primary rounded-none w-fit"
                          onClick={() => setSelectedProduct(product.id)}
                          data-testid={`notify-btn-${product.id}`}
                        >
                          <Bell size={16} className="mr-2" />
                          Notify Me
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-neutral-900 border-neutral-800">
                        <DialogHeader>
                          <DialogTitle className="font-['Syne']">Get Notified</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <p className="text-muted-foreground text-sm">
                            Enter your email and we'll notify you when {product.name} drops.
                          </p>
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            value={notifyEmail}
                            onChange={(e) => setNotifyEmail(e.target.value)}
                            className="rounded-none bg-neutral-800 border-neutral-700"
                            data-testid="notify-email-input"
                          />
                          <Button 
                            onClick={() => handleNotify(product.id)}
                            className="btn-primary rounded-none w-full"
                            data-testid="notify-submit-btn"
                          >
                            Notify Me
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Available Drops */}
        {availableDrops.length > 0 && (
          <section>
            <h2 className="font-['Syne'] text-2xl font-bold mb-8">Available Now</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableDrops.map((product) => (
                <Link 
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="group border border-neutral-800 hover:border-neutral-500 transition-colors"
                  data-testid={`available-drop-${product.id}`}
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/400'}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 bg-[#FF3B30] text-white text-xs uppercase tracking-widest px-3 py-1">
                      Drop
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium group-hover:text-[#FF3B30] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-muted-foreground text-sm mt-1">
                      {product.stock} left in stock
                    </p>
                    <p className="font-bold mt-2">{formatPrice(product.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {!loading && drops.length === 0 && (
          <div className="text-center py-20 border border-neutral-800">
            <h3 className="font-['Syne'] text-2xl font-bold mb-4">No drops available</h3>
            <p className="text-muted-foreground mb-8">
              Check back soon for exclusive limited releases
            </p>
            <Link to="/shop">
              <Button className="btn-primary rounded-none" data-testid="browse-shop-btn">
                Browse Shop
              </Button>
            </Link>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-square bg-neutral-900 animate-pulse" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
